import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  apiRequest,
  clearStoredSession,
  exchangeTokenRequest,
  getErrorMessage,
  loadStoredSession,
  loginMicrosoftRequest,
  loginRequest,
  logoutRequest,
  refreshRequest,
  resetPasswordRequest,
  saveStoredSession,
  verifySsoCodeRequest,
} from '../lib/api';
import { PAGE_LABELS, toUiRole } from '../lib/format';

const AppContext = createContext();

function hasAnyPermission(permissions, expected) {
  return expected.some((permission) => permissions.includes(permission));
}

function getSsoCode() {
  return new URLSearchParams(window.location.search).get('sso');
}

function stripSsoParam() {
  const url = new URL(window.location.href);
  if (url.searchParams.has('sso')) {
    url.searchParams.delete('sso');
    window.history.replaceState({}, '', url);
  }
}

export function AppProvider({ children }) {
  const [session, setSession] = useState(() => loadStoredSession());
  const [currentUser, setCurrentUser] = useState(null);
  const [authStatus, setAuthStatus] = useState(() => (
    getSsoCode() || loadStoredSession()?.accessToken ? 'loading' : 'unauthenticated'
  ));
  const [authView, setAuthView] = useState('login');
  const [resetPasswordUsername, setResetPasswordUsername] = useState('');
  const [activePage, setActivePage] = useState('dashboard');
  const [toast, setToast] = useState({ show: false, msg: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [openModals, setOpenModals] = useState({});
  const [departments, setDepartments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [registrySearch, setRegistrySearch] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar_collapsed') === '1'; } catch { return false; }
  });
  const [dashboardTab, setDashboardTab] = useState('overview');
  const [pendingAction, setPendingAction] = useState(null);

  const toastTimer = useRef(null);
  const confirmResolverRef = useRef(null);
  const sessionRef = useRef(session);
  const refreshPromiseRef = useRef(null);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const showToast = useCallback((msg, type = 'info') => {
    const toastType = type === 'error' || type === 'success' ? type : 'info';
    setToast({ show: true, msg, type: toastType });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => {
      setToast({ show: false, msg: '', type: 'info' });
    }, toastType === 'error' ? 6000 : 3500);
  }, []);

  const confirmAction = useCallback((options = {}) => new Promise((resolve) => {
    confirmResolverRef.current = resolve;
    setConfirmDialog({
      title: options.title || 'ยืนยัน',
      message: options.message || '',
      confirmLabel: options.confirmLabel || 'ยืนยัน',
      cancelLabel: options.cancelLabel || 'ยกเลิก',
      variant: options.variant || 'default',
    });
  }), []);

  const resolveConfirm = useCallback((result) => {
    confirmResolverRef.current?.(result);
    confirmResolverRef.current = null;
    setConfirmDialog(null);
  }, []);

  const openModal = useCallback((id) => {
    setOpenModals((prev) => ({ ...prev, [id]: true }));
  }, []);

  const closeModal = useCallback((id) => {
    setOpenModals((prev) => ({ ...prev, [id]: false }));
  }, []);

  const resetAuthState = useCallback((message) => {
    clearStoredSession();
    setSession(null);
    setCurrentUser(null);
    setDepartments([]);
    setBranches([]);
    setOrganizations([]);
    setUnreadCount(0);
    setActivePage('dashboard');
    setAuthStatus('unauthenticated');
    if (message) {
      showToast(message);
    }
  }, [showToast]);

  const refreshAccessToken = useCallback(async () => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const refreshToken = sessionRef.current?.refreshToken;
    if (!refreshToken) {
      throw new Error('ไม่มี refresh token');
    }

    refreshPromiseRef.current = (async () => {
      const response = await refreshRequest(refreshToken);
      const nextSession = {
        ...sessionRef.current,
        accessToken: response.data.accessToken,
        expiresIn: response.data.expiresIn,
      };

      setSession(nextSession);
      saveStoredSession(nextSession);
      return response.data.accessToken;
    })().finally(() => {
      refreshPromiseRef.current = null;
    });

    return refreshPromiseRef.current;
  }, []);

  const apiFetch = useCallback(async (path, options = {}) => {
    const accessToken = sessionRef.current?.accessToken;
    if (!accessToken) {
      throw new Error('กรุณาเข้าสู่ระบบก่อน');
    }

    try {
      return await apiRequest(path, {
        ...options,
        accessToken,
      });
    } catch (error) {
      if (
        sessionRef.current?.refreshToken &&
        error.status === 401 &&
        (error.code === 'TOKEN_EXPIRED' || error.code === 'UNAUTHORIZED' || !error.code)
      ) {
        try {
          const nextAccessToken = await refreshAccessToken();
          return await apiRequest(path, {
            ...options,
            accessToken: nextAccessToken,
          });
        } catch (refreshError) {
          resetAuthState('Session หมดอายุ กรุณาเข้าสู่ระบบใหม่');
          throw refreshError;
        }
      }

      throw error;
    }
  }, [refreshAccessToken, resetAuthState]);

  const fetchLookups = useCallback(async (customFetch = apiFetch) => {
    const [deptResult, branchResult, orgResult] = await Promise.allSettled([
      customFetch('/departments'),
      customFetch('/branches'),
      customFetch('/organizations'),
    ]);

    if (deptResult.status === 'fulfilled') {
      const data = Array.isArray(deptResult.value.data) ? deptResult.value.data : deptResult.value.data?.items || [];
      setDepartments(data);
    }

    if (branchResult.status === 'fulfilled') {
      const data = Array.isArray(branchResult.value.data) ? branchResult.value.data : branchResult.value.data?.items || [];
      setBranches(data);
    }

    if (orgResult.status === 'fulfilled') {
      const data = Array.isArray(orgResult.value.data) ? orgResult.value.data : orgResult.value.data?.items || [];
      setOrganizations(data);
    }
  }, [apiFetch]);

  const fetchUnreadNotifications = useCallback(async (customFetch = apiFetch) => {
    try {
      const response = await customFetch('/notifications/unread-count');
      const count = (
        response.data?.count ??
        response.data?.unreadCount ??
        response.data?.total ??
        0
      );
      setUnreadCount(count);
      return count;
    } catch {
      setUnreadCount(0);
      return 0;
    }
  }, [apiFetch]);

  const bootstrapAuth = useCallback(async (initialAccessToken) => {
    const accessToken = initialAccessToken || sessionRef.current?.accessToken;
    if (!accessToken) {
      setAuthStatus('unauthenticated');
      return;
    }

    try {
      const meResponse = await apiRequest('/auth/me', { accessToken });
      setCurrentUser(meResponse.data);
      setAuthStatus('authenticated');

      const bootstrapFetch = async (path, options = {}) => apiRequest(path, {
        ...options,
        accessToken,
      });

      void fetchLookups(bootstrapFetch);
      void fetchUnreadNotifications(bootstrapFetch);
    } catch (error) {
      if (sessionRef.current?.refreshToken && (error.status === 401 || error.code === 'TOKEN_EXPIRED')) {
        try {
          const nextAccessToken = await refreshAccessToken();
          const meResponse = await apiRequest('/auth/me', { accessToken: nextAccessToken });
          setCurrentUser(meResponse.data);
          setAuthStatus('authenticated');
          void fetchLookups();
          void fetchUnreadNotifications();
          return;
        } catch {
          resetAuthState();
          return;
        }
      }

      resetAuthState();
    }
  }, [fetchLookups, fetchUnreadNotifications, refreshAccessToken, resetAuthState]);

  const loginWithOneAuth = useCallback(async (ssoCode) => {
    setAuthStatus('loading');
    try {
      // Step 1: Verify the One Password SSO code with HR backend → HR token
      const { token: hrToken } = await verifySsoCodeRequest(ssoCode);

      // Step 2: Exchange HR token for system-register session (username derived from token)
      const response = await exchangeTokenRequest({ hrToken });

      const nextSession = {
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken,
        expiresIn: response.data.expiresIn,
        user: response.data.user,
      };

      setSession(nextSession);
      saveStoredSession(nextSession);
      await bootstrapAuth(nextSession.accessToken);
      setActivePage('dashboard');
      showToast(`เข้าสู่ระบบแล้ว: ${response.data.user.fullName}`);
      return response.data.user;
    } catch (error) {
      setAuthStatus('unauthenticated');
      showToast(getErrorMessage(error, 'เข้าสู่ระบบผ่าน One Auth ไม่สำเร็จ'));
    } finally {
      // Strip ?sso= so a reload never re-triggers the exchange
      stripSsoParam();
    }
  }, [bootstrapAuth, showToast]);

  // Bootstrap auth on mount (or handle a One Auth SSO callback if ?sso= is present)
  const bootstrapRef = useRef(false);
  useEffect(() => {
    if (bootstrapRef.current) return;
    bootstrapRef.current = true;
    const ssoCode = getSsoCode();
    if (ssoCode) {
      void loginWithOneAuth(ssoCode);
    } else {
      void bootstrapAuth();
    }
  }, [bootstrapAuth, loginWithOneAuth]);

  const login = useCallback(async ({ username, password }) => {
    setAuthStatus('loading');
    try {
      // Step 1: Authenticate via HR API (Active Directory)
      const hrResponse = await loginRequest({ username, password });

      // Step 2: Exchange HR token for system-register tokens
      const response = await exchangeTokenRequest({ hrToken: hrResponse.data.token, username });

      const nextSession = {
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken,
        expiresIn: response.data.expiresIn,
        user: response.data.user,
      };

      setSession(nextSession);
      saveStoredSession(nextSession);
      await bootstrapAuth(nextSession.accessToken);
      setActivePage('dashboard');
      showToast(`เข้าสู่ระบบแล้ว: ${response.data.user.fullName}`);
      return response.data.user;
    } catch (error) {
      setAuthStatus('unauthenticated');

      if (
        error.code === 'MUST_RESET_PASSWORD' ||
        error.code === 'MUST_CHANGE_PASSWORD' ||
        error.code === 'PASSWORD_EXPIRED'
      ) {
        setResetPasswordUsername(username);
        setAuthView('resetPassword');
        return;
      }

      throw error;
    }
  }, [bootstrapAuth, showToast]);

  const handleResetPassword = useCallback(async ({ mail, newPassword }) => {
    await resetPasswordRequest({ mail, newPassword });
  }, []);

  const goToLogin = useCallback(() => {
    setAuthView('login');
    setResetPasswordUsername('');
  }, []);

  const loginWithMicrosoft = useCallback(async (idToken) => {
    setAuthStatus('loading');
    try {
      const response = await loginMicrosoftRequest(idToken);

      const nextSession = {
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken,
        expiresIn: response.data.expiresIn,
        user: response.data.user,
      };

      setSession(nextSession);
      saveStoredSession(nextSession);
      await bootstrapAuth(nextSession.accessToken);
      setActivePage('dashboard');
      showToast(`เข้าสู่ระบบแล้ว: ${response.data.user.fullName}`);
      return response.data.user;
    } catch (error) {
      setAuthStatus('unauthenticated');
      throw error;
    }
  }, [bootstrapAuth, showToast]);

  const logout = useCallback(async () => {
    try {
      if (sessionRef.current?.accessToken && sessionRef.current?.refreshToken) {
        await logoutRequest(sessionRef.current.accessToken, sessionRef.current.refreshToken);
      }
    } catch {
      // ignore logout errors and clear client state anyway
    } finally {
      resetAuthState('ออกจากระบบแล้ว');
    }
  }, [resetAuthState]);

  const currentRole = toUiRole(currentUser?.role);
  const permissions = useMemo(() => currentUser?.permissions || [], [currentUser?.permissions]);
  const canCreateSystems = hasAnyPermission(permissions, ['systems.create', 'systems.write']);

  const isPageVisible = useCallback((pageId) => {
    if (!currentUser) {
      return false;
    }

    if (['dashboard', 'registry', 'privacy-settings'].includes(pageId)) {
      return true;
    }

    switch (pageId) {
      case 'register-form':
      case 'register':
        return canCreateSystems;
      case 'import-systems':
        return currentUser.role === 'IT_ADMIN';
      case 'access-request':
        return hasAnyPermission(permissions, ['requests.create', 'requests.manage']);
      case 'onboard':
        return hasAnyPermission(permissions, ['onboard.manage']);
      case 'offboard':
        return hasAnyPermission(permissions, ['offboard.manage']);
      case 'permissions':
        return hasAnyPermission(permissions, ['permissions.manage']);
      case 'smtp-settings':
        return currentUser.role === 'IT_ADMIN';
      case 'checklist-settings':
      case 'exit-interview-settings':
        return currentUser.role === 'IT_ADMIN' || currentUser.role === 'HR';
      default:
        return true;
    }
  }, [canCreateSystems, currentUser, permissions]);

  const toggleSidebar = useCallback(() => setSidebarOpen((prev) => !prev), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem('sidebar_collapsed', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const showPage = useCallback((id) => {
    if (id === 'task-action' || id === 'tasks') {
      setActivePage('dashboard');
      setDashboardTab('summary');
      setSidebarOpen(false);
      return;
    }
    if (id === 'approval') {
      setActivePage('dashboard');
      setDashboardTab('pending');
      setSidebarOpen(false);
      return;
    }

    if (!currentUser || isPageVisible(id)) {
      setActivePage(id);
      setSidebarOpen(false);
      return;
    }

    showToast('คุณไม่มีสิทธิ์เข้าหน้านี้');
  }, [currentUser, isPageVisible, showToast]);

  useEffect(() => {
    if (currentUser && !isPageVisible(activePage)) {
      setActivePage('dashboard');
    }
  }, [activePage, currentUser, isPageVisible]);

  useEffect(() => {
    const handler = (event) => {
      const tagName = event.target.tagName;
      if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') return;

      if (event.key === '1') showPage('dashboard');
      if (event.key === '2') showPage('registry');
      if (event.key === '3') showPage('register-form');
      if (event.key === '4') showPage('task-action');
      if (event.key === '/') {
        event.preventDefault();
        document.getElementById('global-search')?.focus();
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [showPage]);

  const value = useMemo(() => ({
    activePage,
    apiFetch,
    authStatus,
    authView,
    branches,
    canCreateSystems,
    closeModal,
    confirmAction,
    confirmDialog,
    currentRole,
    currentUser,
    dashboardTab,
    resolveConfirm,
    setDashboardTab,
    departments,
    organizations,
    pendingAction,
    setPendingAction,
    goToLogin,
    handleResetPassword,
    isAuthenticated: authStatus === 'authenticated',
    isPageVisible,
    login,
    loginWithMicrosoft,
    logout,
    openModal,
    openModals,
    pages: PAGE_LABELS,
    permissions,
    registrySearch,
    resetPasswordUsername,
    setRegistrySearch,
    showPage,
    showToast,
    sidebarOpen,
    sidebarCollapsed,
    toggleSidebar,
    toggleSidebarCollapsed,
    closeSidebar,
    toast,
    unreadCount,
    refreshUnreadNotifications: fetchUnreadNotifications,
  }), [
    activePage,
    apiFetch,
    authStatus,
    authView,
    branches,
    canCreateSystems,
    closeModal,
    closeSidebar,
    confirmAction,
    confirmDialog,
    currentRole,
    currentUser,
    dashboardTab,
    departments,
    organizations,
    fetchUnreadNotifications,
    goToLogin,
    handleResetPassword,
    isPageVisible,
    login,
    loginWithMicrosoft,
    logout,
    openModal,
    openModals,
    pendingAction,
    permissions,
    registrySearch,
    resetPasswordUsername,
    resolveConfirm,
    showPage,
    showToast,
    sidebarOpen,
    sidebarCollapsed,
    toggleSidebar,
    toggleSidebarCollapsed,
    toast,
    unreadCount,
  ]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
